import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  Text,
  toast,
} from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format, formatDistanceToNowStrict, isAfter, parseISO } from 'date-fns'
import {
  CushionChargePolicyItem,
  useCushionChargePoliciesWorkflowsContextValue,
} from '../../../context/CushionChargePoliciesWorkflowsProvider'
import { useEditCushionChargePolicyFlowContextValue } from '../context/EditCushionChargePolicyFlowProvider'
import {
  EditCushionChargePolicySchema,
  editCushionChargePolicySchema,
} from '../../../schemas/cushionChargePolicy'

function describePolicyStart(startsAt: string) {
  const date = parseISO(startsAt)
  const formatted = format(date, 'MMM d, yyyy h:mm a')
  const distance = formatDistanceToNowStrict(date, { addSuffix: true })
  const label = isAfter(date, new Date()) ? 'Starts' : 'Active since'
  return `${label} ${formatted} (${distance})`
}

export const EditCushionChargePolicyFlowMain = ({
  policy,
}: {
  policy: CushionChargePolicyItem
}) => {
  const {
    editCushionChargePolicyFlow: { isOpen, close },
  } = useCushionChargePoliciesWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useEditCushionChargePolicyFlowContextValue()

  const methods = useForm<EditCushionChargePolicySchema>({
    resolver: zodResolver(editCushionChargePolicySchema),
    values: {
      chargeAfterMinutes: policy.chargeAfterMinutes,
    },
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = methods

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        policyId: policy.id,
        chargeAfterMinutes: data.chargeAfterMinutes,
      },
      {
        onSuccess: () => {
          toast.success('Cushion charge policy updated', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit cushion charge policy
        </Heading>
        <Text mt={2} fontSize={1} color="lightText">
          {describePolicyStart(policy.startsAt)}
        </Text>
        <Box mt={3}>
          <Field
            label="Charge after (minutes)"
            error={errors.chargeAfterMinutes?.message?.toString()}
          >
            <Input
              {...register('chargeAfterMinutes')}
              type="number"
              min={0}
              step={1}
              aria-label="Charge after minutes"
            />
          </Field>
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
